import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as R from 'ramda';
import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { withRouter } from 'react-router-dom';
import {
  withCoreComponent, withModalActions, withLocale, withUserAgent
} from 'core/hocs';
import { GamesFilters as GamesFiltersCore, ChangeRoute, FormattedTag } from 'core/components';
import { GAME_CATEGORY_FILTER, DEVICES_TYPES } from 'core/constants';
import {
  isEmptyOrNil, createUrlSlug, setDocumentScrollTopPosition, wrapRoute2Locale
} from 'core/helpers';
import { GameCard } from 'components/game-card/game-card';
import { ProducersModal } from 'components/modals/producers-modal/producers-modal';
import { GamesCategoriesModal } from 'components/modals/games-categories-modal/games-categories-modal';
import { IconProducerChecked } from 'components/icons/icon-producer-checked/icon-producer-checked';
import { IconProducersClose } from 'components/icons/icon-producers-close/icon-producers-close';
import { IconGamesNotFound } from 'components/icons/icon-games-not-found/icon-games-not-found';
import { GameTags } from 'components/game-tags/game-tags';

import { GamesFiltersMenu } from './games-filters-menu';
import { GamesFiltersMenuSticky } from './games-filters-menu-sticky';
import { GamesSearchInput } from './games-search-input';
import { producerIcons, UnknownProducerIcon } from './producer-icons';
import { MODAL_IDS } from '../../constants';

import './games-filters.scss';

const GAMES_SEARCH_MOBILE_OPEN = 'games-search-mobile-open';

const produsersToSortOrderIndexesMapping = {
  EGT: 1,
  Evolution: 2,
  Pragmatic: 3,
  'Pragmatic Live': 4,
  'Ruby Play': 5,
  Playson: 6,
  Ezugi: 7,
  'Nolimit City': 8,
  'Red Rake Gaming': 9,
  Netent: 10,
  Tomhorn: 11,
  Habanero: 12,
  Wazdan: 13,
  Amatic: 14,
  Evoplay: 15,
  'Booming Games': 16,
  Endorphina: 17,
  'Betsoft Gaming': 18,
  '1x2 Gaming': 19,
  Isoftbet: 20,
  PgSoft: 21,
  'Push Gaming': 22,
  'Red Tiger': 23,
  Quickspin: 24,
  Spribe: 25,
  HackSaw: 26,
  'Big Time Gaming': 27,
  'Play\'n Go': 28,
  Microgaming: 29,
  'Relax Gaming': 30,
  GameArt: 31,
  'GOLDEN HERO GROUP': 32,
  ORYX: 33,
  Yggdrasil: 34,
  GAMOMAT: 35,
  Booongo: 36,
  Spinomenal: 37,
  KALAMBA: 38,
  BGAMING: 39,
  'Games Inc': 40,
  'G Games': 41,
  'Golden Hero Group': 42,
  GIVME: 43,
  Swintt: 44,
  Genii: 45,
};

const sortProducersList = (producers = []) => (
  R.sort((a, b) => {
    const defaultVal = 1000;
    const targetA = produsersToSortOrderIndexesMapping[a] || defaultVal;
    const targetB = produsersToSortOrderIndexesMapping[b] || defaultVal;

    return targetA - targetB;
  }, producers || [])
);

class GamesFiltersUI extends Component {
  static propTypes = {
    isUserLoggedIn: PropTypes.bool.isRequired,
    openGame: PropTypes.func.isRequired,
    toggleFavoriteGame: PropTypes.func.isRequired,
    setSearchValue: PropTypes.func.isRequired,
    setProducers: PropTypes.func.isRequired,
    // clearAllFilters: PropTypes.func.isRequired,
    games: PropTypes.arrayOf(PropTypes.shape),
    filteredByCategoryGames: PropTypes.arrayOf(PropTypes.shape),
    producers: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.shape({
      category: PropTypes.string,
      searchValue: PropTypes.string,
      producers: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    showMore: PropTypes.func.isRequired,
    isShowMoreAvailable: PropTypes.bool.isRequired,
    gamesFound: PropTypes.number.isRequired,
    jackpotsIsInProgress: PropTypes.bool.isRequired,
    favoriteGamesIsInProgress: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    history: PropTypes.shape().isRequired,
    locale: PropTypes.string.isRequired,
    currentModal: PropTypes.shape({
      id: PropTypes.string,
      data: PropTypes.any,
    }).isRequired,
    deviceType: PropTypes.string.isRequired,
  };

  static defaultProps = {
    games: [],
    filteredByCategoryGames: [],
    producers: [],
  };

  gameFiltersMenuRef = createRef();
  producersRef = createRef();

  state = {
    isOverlayOpen: false,
    isShowProducers: false,
    isOpenSearch: false
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { isOverlayOpen } = this.state;

    if (isOverlayOpen) {
      setDocumentScrollTopPosition();
    }

    if (!isOverlayOpen) {
      document.body.classList.remove(GAMES_SEARCH_MOBILE_OPEN);
    }

    const { isShowProducers } = this.state;

    if (isShowProducers && !prevState.isShowProducers && this.gameFiltersMenuRef.current) {
      this.gameFiltersMenuRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    if (!prevState.isShowProducers && isShowProducers) {
      this.clickSubscribtion = fromEvent(document, 'click').pipe(
        filter(event => this.producersRef && !this.producersRef.current.contains(event.target)),
      ).subscribe(() => {
        this.toggleProducers();
      });
    }

    if (prevState.isShowProducers && !isShowProducers && this.clickSubscribtion) {
      this.clickSubscribtion.unsubscribe();
    }
  }

  componentWillUnmount() {
    if (this.clickSubscribtion) {
      this.clickSubscribtion.unsubscribe();
    }

    const { isOverlayOpen } = this.state;

    if (isOverlayOpen) {
      document.body.classList.remove(GAMES_SEARCH_MOBILE_OPEN);
    }
  }

  setProducers = ({ currentTarget: { dataset: { producer } } }) => {
    const { filters: { producers }, setProducers } = this.props;
    const newProducers = R.ifElse(
      R.includes(producer),
      R.without([producer]),
      R.append(producer)
    )(producers);

    setProducers(newProducers);
  };

  onChange = (e) => {
    const { value } = e.target;
    const { setSearchValue } = this.props;

    setSearchValue(value);
  };

  toggleProducers = () => {
    this.setState(prevState => ({
      isShowProducers: !prevState.isShowProducers,
    }));
  }

  clearProducers = (e) => {
    const { setProducers } = this.props;

    e.stopPropagation();
    setProducers([]);
  };

  clearSearchValue = () => {
    const { setSearchValue } = this.props;
    setSearchValue('');
    this.toggleSearchInput();
  };

  toggleSearchInput = () => {
    const { history, locale, deviceType } = this.props;
    const isMobile = deviceType === DEVICES_TYPES.MOBILE;
    const { isOverlayOpen } = this.state;

    if (isMobile) { (isOverlayOpen ? this.onOverlayClose : this.onOverlayOpen)(); } else {
      this.setState(prevState => ({
        isOpenSearch: !prevState.isOpenSearch,
      }));
    }

    return history.push(wrapRoute2Locale('/games', locale));
  };

  onOverlayOpen = () =>
    this.setState(({ isOverlayOpen: true }), () => document.body.classList.add(GAMES_SEARCH_MOBILE_OPEN));

  onOverlayClose = () => this.setState({ isOverlayOpen: false })

  render() {
    const {
      isUserLoggedIn,
      openGame,
      toggleFavoriteGame,
      producers,
      filters: {
        category: filterCategory,
        producers: filterProducers,
        searchValue,
      },
      currentModal: { id },
      games,
      gamesFound,
      filteredByCategoryGames: filteredGames,
      showMore,
      isShowMoreAvailable,
      jackpotsIsInProgress,
      favoriteGamesIsInProgress,
      setProducers,
      // clearAllFilters,
      closeModal,
      deviceType,
    } = this.props;

    const {
      isOpenSearch, isOverlayOpen, isShowProducers
    } = this.state;

    // const isAllGamesFilter = isAllGames(filterCategory);
    const isJackpotsFilter = filterCategory === GAME_CATEGORY_FILTER.JACKPOTS;
    const isFavoriteFilter = filterCategory === GAME_CATEGORY_FILTER.FAVORITE;

    const isThematicCategoriesVisible = [
      GAME_CATEGORY_FILTER.ALL,
    ].includes(filterCategory);
    const sortedProducersList = sortProducersList(producers);

    if (!isUserLoggedIn && filterCategory === GAME_CATEGORY_FILTER.FAVORITE) {
      return <ChangeRoute to="/games" />;
    }

    const isMobile = deviceType === DEVICES_TYPES.MOBILE;

    return (
      <div
        className={classNames('games-filters',
          { 'overlay-open d-md-none position-absolute w-100 min-vh-100 overflow-auto': isOverlayOpen },
          { 'position-relative': !isOverlayOpen },
          { 'producers-modal-open': id === MODAL_IDS.PRODUCERS })}
        ref={this.gameFiltersMenuRef}
      >

        <GamesFiltersMenu
          filterCategory={filterCategory}
          toggleProducers={this.toggleProducers}
          filterProducers={filterProducers}
          clearProducers={this.clearProducers}
          toggleSearchInput={this.toggleSearchInput}
          isOpenSearch={isOpenSearch}
          isUserLoggedIn={isUserLoggedIn}
          className={isOverlayOpen ? 'd-none' : ''}
        />

        {isMobile && isOverlayOpen && (
          <GamesSearchInput
            onChange={this.onChange}
            clearSearchValue={this.clearSearchValue}
            searchValue={searchValue}
            openProducersModal={this.openProducersModal}
            onClick={this.onOverlayOpen}
            isOverlayOpen={isOverlayOpen}
            autoFocus
          />
        )}

        {!isMobile && isOpenSearch && (
          <GamesSearchInput
            onChange={this.onChange}
            clearSearchValue={this.clearSearchValue}
            searchValue={searchValue}
            autoFocus
          />
        )}

        {isShowProducers && (
          <div ref={this.producersRef} className="producers-wrapper bg-dark-2 position-relative rounded px-3 pt-3_75 mb-3 pb-6_5 mx-2 mt-2_25">
            {!isEmptyOrNil(sortedProducersList) && sortedProducersList.map((producer) => {
              const Icon = producerIcons[createUrlSlug(producer)] || UnknownProducerIcon;
              const isIncludeProducer = filterProducers.includes(producer);

              return (
                <div
                  key={producer}
                  role="button"
                  tabIndex="0"
                  onClick={this.setProducers}
                  onKeyPress={this.setProducers}
                  data-producer={producer}
                  className={classNames('d-flex align-items-center game-producer px-0_25 mr-2_25 border-bottom border-gray-10-20', {
                    'producer-not-include': !isIncludeProducer,
                    'has-filter-producers': filterProducers.length > 0,
                  })}
                >
                  <Icon className="producer-icon" />

                  <span className={classNames('ml-1_5 game-producer-name text-white letter-spacing-default', {
                    'text-gray-30': filterProducers.length > 0 && !isIncludeProducer,
                    'text-primary font-weight-semi-bold': isIncludeProducer,
                  })}
                  >
                    {producer}
                  </span>

                  {isIncludeProducer && <IconProducerChecked className="rounded ml-auto" />}
                </div>
              );
            })}

            {!isEmptyOrNil(filterProducers) && (
              <span
                role="button"
                tabIndex="0"
                className="clear-all cursor-pointer position-absolute"
                onClick={this.clearProducers}
                onKeyPress={this.clearProducers}
              >
                <IconProducersClose />

                <FormattedTag
                  className="h5 font-weight-medium ml-0_25"
                  id="clear-all"
                />
              </span>
            )}
          </div>
        )}

        {isThematicCategoriesVisible && (
          <GameTags className="mb-4 mb-lg-5 mt-2 mt-lg-3 px-2 px-sm-2_5 px-lg-5" />
        )}

        {(isJackpotsFilter && jackpotsIsInProgress) || (isFavoriteFilter && favoriteGamesIsInProgress) ? (
          <span />
        ) : (
          <div
            className={classNames('games-filters-list pb-2 pb-md-4_5 mt-2 mt-md-3 px-2 px-sm-2_5 px-lg-5', {
              'd-block': !gamesFound,
            })}
          >
            {!isEmptyOrNil(games) && games.map((game, idx) => (
              <GameCard
                // eslint-disable-next-line react/no-array-index-key
                key={`${game.gameId}-${game.producer}-${idx}`}
                {...game}
                isUserLoggedIn={isUserLoggedIn}
                toggleFavoriteGame={toggleFavoriteGame}
                openGame={openGame}
                withJackpotAmount
              />
            ))}

            {!gamesFound && (
              <div className="d-flex flex-column align-items-center mt-9_5">
                <IconGamesNotFound className="mb-2_75" />

                <FormattedTag
                  id="search.results.not-found"
                  className="h4 font-weight-semi-bold"
                />

                <FormattedTag
                  id="search.results.not-found.advice"
                  className="text-gray-10"
                />
              </div>
            )}
          </div>
        )}

        <div className="w-100 px-2 px-sm-2_5 px-lg-5 pb-3">
          {isShowMoreAvailable && (
            <FormattedTag
              tag={Button}
              id="show-more"
              color="primary"
              className="mt-2 d-block mx-auto"
              onClick={showMore}
            />
          )}
        </div>

        <GamesFiltersMenuSticky
          filterCategory={filterCategory}
          filterProducers={filterProducers}
          toggleSearchInput={this.toggleSearchInput}
          isOpenSearch={isOverlayOpen}
          clearSearchValue={this.clearSearchValue}
          onChange={this.onChange}
          searchValue={searchValue}
          toggleProducers={this.toggleProducers}
          isMobile={isMobile}
        />

        <ProducersModal
          filteredGames={filteredGames}
          producers={producers}
          filterProducers={filterProducers}
          setProducers={setProducers}
          // clearAllFilters={clearAllFilters}
          closeModal={closeModal}
        />

        <GamesCategoriesModal
          filterCategory={filterCategory}
        />
      </div>
    );
  }
}

export const GamesFilters = withCoreComponent(
  GamesFiltersCore,
  withRouter(
    withLocale(
      withUserAgent(
        withModalActions(
          GamesFiltersUI,
        ),
      ),
    ),
  ),
);
